import React, { useEffect, useState } from 'react'
import {
  Typography,
  Card,
  Col,
  Row,
  Select,
  Space,
} from 'antd'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchPersonalStats, fetchTeamStats } from '../store/slices/statsSlice'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']

function Statistics() {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { personalStats, teamStats, loading } = useAppSelector((state) => state.stats)
  const [period, setPeriod] = useState('today')

  useEffect(() => {
    dispatch(fetchPersonalStats(period))
    if (user?.is_admin) {
      dispatch(fetchTeamStats(period))
    }
  }, [dispatch, period, user?.is_admin])

  const sourceData = personalStats ? [
    { name: 'Palladium', value: personalStats.by_source.palladium },
    { name: 'CRM', value: personalStats.by_source.crm },
    { name: 'API', value: personalStats.by_source.api },
  ].filter(item => item.value > 0) : []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Typography.Title level={2}>Statistics</Typography.Title>
        
        <Select
          value={period}
          onChange={(value) => setPeriod(value)}
          style={{ minWidth: 120 }}
        >
          <Select.Option value="today">Today</Select.Option>
          <Select.Option value="week">This Week</Select.Option>
          <Select.Option value="month">This Month</Select.Option>
        </Select>
      </div>

      {/* Queue Status - visible to all users */}
      {personalStats && (
        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Card title="Queue Status">
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                    <Typography.Title level={2} style={{ color: '#52c41a', margin: 0 }}>
                      {personalStats.queue_status.available}
                    </Typography.Title>
                    <Typography.Text type="secondary">Available</Typography.Text>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                    <Typography.Title level={2} style={{ color: '#faad14', margin: 0 }}>
                      {personalStats.queue_status.locked}
                    </Typography.Title>
                    <Typography.Text type="secondary">Locked</Typography.Text>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                    <Typography.Title level={2} style={{ color: '#1890ff', margin: 0 }}>
                      {personalStats.queue_status.total}
                    </Typography.Title>
                    <Typography.Text type="secondary">Total</Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={24}>
        {user?.is_admin && teamStats && (
          <>
            <Col xs={24}>
              <Card title={`${period === 'today' ? "Today's" : period === 'week' ? "This Week's" : "This Month's"} Pace - Speed per Person`}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Completed</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Successful</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Success Rate</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Rejection Rate</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Release Rate</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Emails Found</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Pace (successful/hr)</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Active Time</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamStats.leaderboard.map((user: any, index: number) => (
                        <tr key={user.email} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '12px' }}>
                            <Typography.Text strong>{user.email.split('@')[0]}</Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ color: '#1890ff', fontWeight: 600 }}>
                              {user.completed}
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ color: '#52c41a', fontWeight: 600 }}>
                              {user.successful || 0}
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ 
                              color: user.success_rate > 0.7 ? '#52c41a' : user.success_rate > 0.4 ? '#faad14' : '#ff4d4f' 
                            }}>
                              {(user.success_rate * 100).toFixed(1)}%
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ 
                              color: user.rejection_rate > 0.3 ? '#ff4d4f' : user.rejection_rate > 0.1 ? '#faad14' : '#52c41a' 
                            }}>
                              {(user.rejection_rate * 100).toFixed(1)}%
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ 
                              color: user.release_rate > 0.3 ? '#ff4d4f' : user.release_rate > 0.1 ? '#faad14' : '#52c41a' 
                            }}>
                              {(user.release_rate * 100).toFixed(1)}%
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{ color: '#722ed1', fontWeight: 600 }}>
                              {user.total_emails || 0}
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text style={{
                              color: user.pace_per_hour >= 5 ? '#52c41a' : user.pace_per_hour >= 3 ? '#faad14' : user.pace_per_hour > 0 ? '#1890ff' : '#999',
                              fontWeight: 600
                            }}>
                              {user.pace_per_hour || 0} successful/hr
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Typography.Text type="secondary">
                              {user.active_hours}h
                            </Typography.Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {user.pace_per_hour >= 5 ? (
                              <span style={{ color: '#52c41a' }}>🚀 Fast</span>
                            ) : user.pace_per_hour >= 3 ? (
                              <span style={{ color: '#faad14' }}>⚡ Active</span>
                            ) : user.pace_per_hour > 0 ? (
                              <span style={{ color: '#1890ff' }}>📈 Slow</span>
                            ) : (
                              <span style={{ color: '#999' }}>⏸️ Idle</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {teamStats.leaderboard.length > 0 && (() => {
                        const totals = teamStats.leaderboard.reduce((acc: any, u: any) => ({
                          completed: acc.completed + (u.completed || 0),
                          successful: acc.successful + (u.successful || 0),
                          total_emails: acc.total_emails + (u.total_emails || 0),
                          total_activity: acc.total_activity + (u.total_activity || 0),
                          active_seconds: acc.active_seconds + ((parseFloat(u.active_hours) || 0) * 3600),
                        }), { completed: 0, successful: 0, total_emails: 0, total_activity: 0, active_seconds: 0 })
                        const successRate = totals.completed > 0 ? totals.successful / totals.completed : 0
                        const rejectionRate = totals.completed > 0 ? (totals.completed - totals.successful) / totals.completed : 0
                        const releaseRate = totals.total_activity > 0 ? (totals.total_activity - totals.completed) / totals.total_activity : 0
                        const activeHours = totals.active_seconds / 3600
                        const pace = activeHours > 0 ? (totals.successful / activeHours).toFixed(1) : 0
                        return (
                          <tr style={{ borderTop: '3px solid #1890ff', backgroundColor: '#e6f7ff' }}>
                            <td style={{ padding: '12px' }}>
                              <Typography.Text strong style={{ fontSize: '14px' }}>📊 TOTAL</Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: '#1890ff', fontWeight: 700, fontSize: '16px' }}>
                                {totals.completed}
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: '#52c41a', fontWeight: 700, fontSize: '16px' }}>
                                {totals.successful}
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: successRate > 0.7 ? '#52c41a' : successRate > 0.4 ? '#faad14' : '#ff4d4f', fontWeight: 600 }}>
                                {(successRate * 100).toFixed(1)}%
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: rejectionRate > 0.3 ? '#ff4d4f' : rejectionRate > 0.1 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
                                {(rejectionRate * 100).toFixed(1)}%
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: releaseRate > 0.3 ? '#ff4d4f' : releaseRate > 0.1 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
                                {(releaseRate * 100).toFixed(1)}%
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ color: '#722ed1', fontWeight: 700, fontSize: '16px' }}>
                                {totals.total_emails}
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text style={{ fontWeight: 600 }}>
                                {pace} successful/hr
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <Typography.Text type="secondary">
                                {activeHours.toFixed(1)}h
                              </Typography.Text>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              —
                            </td>
                          </tr>
                        )
                      })()}
                    </tbody>
                  </table>
                  {teamStats.leaderboard.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                      {`No activity ${period === 'today' ? 'today' : period === 'week' ? 'this week' : 'this month'}`}
                    </div>
                  )}
                </div>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title={`${period === 'today' ? "Today's" : period === 'week' ? "This Week's" : "This Month's"} Summary`}>
                <div style={{ textAlign: 'center' }}>
                  <Typography.Title level={2} style={{ color: '#1890ff', margin: 0 }}>
                    {teamStats.total_completed_period || teamStats.total_completed_today || 0}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {`Total Leads Completed ${period === 'today' ? 'Today' : period === 'week' ? 'This Week' : 'This Month'}`}
                  </Typography.Text>
                </div>
              </Card>
            </Col>
          </>
        )}

        {/* Personal Statistics - Show at bottom for admins, at top for regular users */}
        {!user?.is_admin && (
          <>
            <Col xs={24} md={12}>
              <Card title="Personal Performance">
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                      <Typography.Title level={3} style={{ color: '#1890ff', margin: 0 }}>
                        {personalStats?.total_worked_on || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Total Worked On</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                      <Typography.Title level={3} style={{ color: '#faad14', margin: 0 }}>
                        {personalStats?.leads_released || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Released</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0', marginTop: 16 }}>
                      <Typography.Title level={3} style={{ color: '#52c41a', margin: 0 }}>
                        {personalStats?.leads_completed || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Completed</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0', marginTop: 16 }}>
                      <Typography.Title level={3} style={{ color: '#52c41a', margin: 0 }}>
                        {((personalStats?.success_rate || 0) * 100).toFixed(1)}%
                      </Typography.Title>
                      <Typography.Text type="secondary">Success Rate</Typography.Text>
                    </div>
                  </Col>
                  <Col span={24} style={{ marginTop: 16 }}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                      <Typography.Title level={3} style={{ color: '#722ed1', margin: 0 }}>
                        {personalStats?.total_emails_found || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Emails Found</Typography.Text>
                    </div>
                  </Col>
                  <Col span={24} style={{ marginTop: 16 }}>
                    <div style={{ padding: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                      <Typography.Text strong>Summary: </Typography.Text>
                      <Typography.Text>
                        {personalStats?.leads_completed || 0} completed + {personalStats?.leads_released || 0} released{' '}
                        = {((personalStats?.leads_completed || 0) + (personalStats?.leads_released || 0))}{' '}
                        out of {personalStats?.total_worked_on || 0} leads worked on
                      </Typography.Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title="Leads by Source">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </>
        )}

        {/* Admin Personal Statistics - Show at bottom */}
        {user?.is_admin && (
          <>
            <Col xs={24}>
              <Typography.Title level={3} style={{ marginTop: 32, marginBottom: 16 }}>
                My Personal Statistics
              </Typography.Title>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Personal Performance">
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                      <Typography.Title level={3} style={{ color: '#1890ff', margin: 0 }}>
                        {personalStats?.total_worked_on || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Total Worked On</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
                      <Typography.Title level={3} style={{ color: '#faad14', margin: 0 }}>
                        {personalStats?.leads_released || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Released</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0', marginTop: 16 }}>
                      <Typography.Title level={3} style={{ color: '#52c41a', margin: 0 }}>
                        {personalStats?.leads_completed || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Completed</Typography.Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #f0f0f0', marginTop: 16 }}>
                      <Typography.Title level={3} style={{ color: '#52c41a', margin: 0 }}>
                        {((personalStats?.success_rate || 0) * 100).toFixed(1)}%
                      </Typography.Title>
                      <Typography.Text type="secondary">Success Rate</Typography.Text>
                    </div>
                  </Col>
                  <Col span={24} style={{ marginTop: 16 }}>
                    <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                      <Typography.Title level={3} style={{ color: '#722ed1', margin: 0 }}>
                        {personalStats?.total_emails_found || 0}
                      </Typography.Title>
                      <Typography.Text type="secondary">Emails Found</Typography.Text>
                    </div>
                  </Col>
                  <Col span={24} style={{ marginTop: 16 }}>
                    <div style={{ padding: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                      <Typography.Text strong>Summary: </Typography.Text>
                      <Typography.Text>
                        {personalStats?.leads_completed || 0} completed + {personalStats?.leads_released || 0} released{' '}
                        = {((personalStats?.leads_completed || 0) + (personalStats?.leads_released || 0))}{' '}
                        out of {personalStats?.total_worked_on || 0} leads worked on
                      </Typography.Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title="Leads by Source">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </>
        )}
      </Row>
    </div>
  )
}

export default Statistics